import type { KmDomain, KmEntityType } from '@/entities/ontology';

/** Ключ группы типов вне доменов — не пересекается с кодами доменов (они в snake_case) */
export const NO_DOMAIN_GROUP_KEY = '__no-domain__';

/**
 * Группа списка типов сущностей — один домен либо все типы вне доменов.
 */
export type EntityTypeDomainGroup = {
  /** Код домена либо {@link NO_DOMAIN_GROUP_KEY} */
  key: string;
  /** Заголовок группы: название домена, для домена вне справочника — его код */
  label: string;
  entityTypes: KmEntityType[];
};

/**
 * Раскладывает типы сущностей по доменам, сохраняя порядок справочника доменов.
 * Домены без подходящих типов группами не становятся, типы вне доменов идут последней группой.
 */
export const groupEntityTypesByDomain = (
  entityTypes: KmEntityType[],
  domains: KmDomain[],
  noDomainLabel: string,
): EntityTypeDomainGroup[] => {
  const byDomainCode = new Map<string, KmEntityType[]>();
  const withoutDomain: KmEntityType[] = [];

  for (const entityType of entityTypes) {
    if (!entityType.domainCode) {
      withoutDomain.push(entityType);
      continue;
    }
    const group = byDomainCode.get(entityType.domainCode);
    if (group) {
      group.push(entityType);
    } else {
      byDomainCode.set(entityType.domainCode, [entityType]);
    }
  }

  const groups: EntityTypeDomainGroup[] = [];

  // Сначала домены в порядке справочника (сервер отдаёт их по названию)
  for (const domain of domains) {
    const domainTypes = byDomainCode.get(domain.code);
    if (domainTypes) {
      groups.push({ key: domain.code, label: domain.name, entityTypes: domainTypes });
      byDomainCode.delete(domain.code);
    }
  }

  // Домен, которого нет в справочнике (например, удалён): группу подписываем его кодом
  for (const [domainCode, domainTypes] of byDomainCode) {
    groups.push({ key: domainCode, label: domainCode, entityTypes: domainTypes });
  }

  if (withoutDomain.length) {
    groups.push({ key: NO_DOMAIN_GROUP_KEY, label: noDomainLabel, entityTypes: withoutDomain });
  }

  return groups;
};
